import { useState } from 'react';
import { ReactComponent as UserIcon } from 'icons/user-filled-icon.svg';
import { format, isToday, isYesterday } from 'date-fns';
import clsx from 'clsx';
import SortButton from './sort-button';
import FilterDropdown from './filter-dropdown';
import { useMemo } from 'react';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useFliptBoolean } from '@flipt-io/flipt-client-react';

const FILTER_LABEL = {
  read: 'Read',
  unread: 'Unread',
  today: 'Hari Ini',
  'this-week': 'Minggu Ini',
  'this-month': 'Bulan Ini',
};

const Sidebar = ({
  inbox,
  onInboxSelect,
  chatView,
  chatType,
  onSelectView,
  onSelectType,
  chatHistory,
  unreadCount,
  selectedInbox,
  setSelectedInbox,
}) => {
  const formatChatTime = (createdAt) => {
    const date = new Date(createdAt);

    if (isToday(date)) {
      return format(date, 'HH:mm');
    } else if (isYesterday(date)) {
      return 'Yesterday';
    } else if (date.getFullYear() === new Date().getFullYear()) {
      return format(date, 'MMM d');
    } else {
      return format(date, 'dd/MM/yyyy');
    }
  };

  const [searchQuery, setSearchQuery] = useState('');
  const [order, setOrder] = useState('');
  const [range, setRange] = useState({ from: undefined, to: undefined });

  const user = useSelector((state) => state.user.profile);
  const isCarCharterEnabled = useFliptBoolean('FLAG_CHARTER', false, user?.id || 'anonymous');

  const rooms = [
    { label: 'Order', type: 'cs-order' },
    { label: 'Driver', type: 'cs-driver' },
    { label: 'User', type: 'cs-customer' },
    ...(isCarCharterEnabled ? [{ label: 'Driver and Customer Chat', type: 'customer-driver' }] : []),
  ];

  const getCount = (type) => unreadCount.find((item) => item.room_type === type)?.count || 0;

  const [filter, setFilter] = useState('all');

  const filteredAndSortedHistory = useMemo(() => {
    if (!chatHistory) return [];

    let result = [...chatHistory];

    if (searchQuery) {
      result = result.filter((item) => item.room?.name?.toLowerCase().includes(searchQuery.toLowerCase()));
    }

    const now = new Date();

    if (filter === 'read') {
      result = result.filter((msg) => msg.message.is_read);
    } else if (filter === 'unread') {
      result = result.filter((msg) => !msg.message.is_read);
    } else if (filter === 'today') {
      result = result.filter((msg) => new Date(msg.created_at).toDateString() === now.toDateString());
    } else if (filter === 'this-week') {
      const weekAgo = new Date();
      weekAgo.setDate(now.getDate() - 7);
      result = result.filter((msg) => new Date(msg.created_at) >= weekAgo);
    } else if (filter === 'this-month') {
      const monthAgo = new Date();
      monthAgo.setMonth(now.getMonth() - 1);
      result = result.filter((msg) => new Date(msg.created_at) >= monthAgo);
    } else if (filter === 'custom-date') {
      if (range?.from && range?.to) {
        result = result.filter((msg) => new Date(msg.created_at) >= range.from && new Date(msg.created_at) <= range.to);
      }

      if (range?.from && !range?.to) {
        result = result.filter((msg) => new Date(msg.created_at).toDateString() === range.from.toDateString());
      }
    }

    const sorted = [...result].sort((a, b) =>
      order === 'asc'
        ? new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        : new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );

    return sorted;
  }, [chatHistory, searchQuery, filter, order, range]);

  const filteredAndSortedInbox = useMemo(() => {
    let result = [...inbox];

    if (searchQuery) {
      result = result.filter((item) => item.room?.name?.toLowerCase().includes(searchQuery.toLowerCase()));
    }

    const now = new Date();

    if (filter === 'read') {
      result = result.filter((msg) => msg.message.is_read);
    } else if (filter === 'unread') {
      result = result.filter((msg) => !msg.message.is_read);
    } else if (filter === 'today') {
      result = result.filter((msg) => new Date(msg.created_at).toDateString() === now.toDateString());
    } else if (filter === 'this-week') {
      const weekAgo = new Date();
      weekAgo.setDate(now.getDate() - 7);
      result = result.filter((msg) => new Date(msg.created_at) >= weekAgo);
    } else if (filter === 'this-month') {
      const monthAgo = new Date();
      monthAgo.setMonth(now.getMonth() - 1);
      result = result.filter((msg) => new Date(msg.created_at) >= monthAgo);
    } else if (filter === 'custom-date') {
      if (range?.from && range?.to) {
        result = result.filter((msg) => new Date(msg.created_at) >= range.from && new Date(msg.created_at) <= range.to);
      }

      if (range?.from && !range?.to) {
        result = result.filter((msg) => new Date(msg.created_at).toDateString() === range.from.toDateString());
      }
    }

    const sorted = [...result].sort((a, b) =>
      order === 'asc'
        ? new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        : new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );

    return sorted;
  }, [inbox, searchQuery, filter, order, range]);

  useEffect(() => {
    setSelectedInbox(null);
  }, [searchQuery, filter, order, range]);

  const selectedDate = () => {
    if (range?.from && range?.to) {
      const start = format(new Date(range.from), 'dd MMMM yyyy');
      const end = format(new Date(range?.to), 'dd MMMM yyyy');
      return `${start} - ${end}`;
    }

    if (range?.from && !range?.to) {
      return format(new Date(range.from), 'dd MMMM yyyy');
    }
  };

  const handleClearFilter = () => {
    setFilter('all');
    setRange({ from: undefined, to: undefined });
  };

  return (
    <div
      className={clsx('chat__sidebar', {
        'chat__sidebar--wide': isCarCharterEnabled,
      })}
    >
      <div className="chat__sidebar-header">
        <div
          className={clsx('tabs', {
            'scroll-tabs': isCarCharterEnabled,
          })}
        >
          {rooms.map((room) => (
            <button
              type="button"
              key={room.type}
              className={`tab-button-type ${chatType === room.type ? 'tab-button-type__active' : ''}`}
              id={room.type}
              onClick={onSelectType}
            >
              {room.label}
              <span className="tabs-badge">{getCount(room.type)}</span>
            </button>
          ))}
        </div>

        <div className="tabs-wrapper">
          <div className="tabs">
            <button
              className={clsx('tab-button', {
                'tab-button__active': chatView === 'online',
                'tab-button--auto': isCarCharterEnabled,
              })}
              id="online"
              onClick={onSelectView}
            >
              Online
            </button>
            <button
              className={clsx('tab-button', {
                'tab-button__active': chatView === 'history',
                'tab-button--auto': isCarCharterEnabled,
              })}
              id="history"
              onClick={onSelectView}
            >
              History
            </button>

            {isCarCharterEnabled ? (
              <div className="filter-btn-wrapper">
                <SortButton order={order} setOrder={setOrder} />
                <FilterDropdown filter={filter} setFilter={setFilter} setRange={setRange} />
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div className="chat__sidebar-wrapper">
        <div className="search-input">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search..."
            className="search-input__field"
          />
        </div>

        {filter !== 'all' && filter !== 'custom-date' ? (
          <div className="selected-filter-wrapper">
            <button className="selected-filter">
              <>
                <span>{FILTER_LABEL[filter]}</span>
                <span className="close-icon" onClick={handleClearFilter}>
                  <svg width="8" height="9" viewBox="0 0 8 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M0.75 7.30375L3.80842 4.02687L6.86683 7.30375M6.86683 0.75L3.80783 4.02687L0.75 0.75"
                      stroke="black"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </>
            </button>
          </div>
        ) : null}

        {filter === 'custom-date' && range?.from ? (
          <div className="selected-filter-wrapper">
            <button className="selected-filter">
              <>
                <span>{selectedDate()}</span>
                <span className="close-icon" onClick={handleClearFilter}>
                  <svg width="8" height="9" viewBox="0 0 8 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M0.75 7.30375L3.80842 4.02687L6.86683 7.30375M6.86683 0.75L3.80783 4.02687L0.75 0.75"
                      stroke="black"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </>
            </button>
          </div>
        ) : null}

        <div className="tabs-content">
          {/* ONLINE */}
          <div className={`tab-page ${chatView === 'online' ? 'tab-page__active' : ''}`}>
            <div className="chat__sidebar-chat-list">
              {filteredAndSortedInbox.length ? (
                filteredAndSortedInbox?.map((item) => (
                  <div
                    onClick={() => onInboxSelect(item)}
                    key={item.message.user_id}
                    className={clsx(
                      'chat__sidebar-chat-item',
                      item.room.id === selectedInbox?.room.id && 'chat__sidebar-chat-item__selected',
                      item.message.is_read === false && 'chat__sidebar-chat-item__unread',
                    )}
                  >
                    <div className="chat__sidebar-user-icon">
                      <UserIcon />
                    </div>
                    <div className="chat__sidebar-chat-info">
                      <div className="chat__sidebar-info-item">
                        <span className="chat__sidebar-info-item__chat-id">{item.room.name}</span>
                        <span className="chat__sidebar-info-item__chat-time">
                          {formatChatTime(item.message.created_at)}
                        </span>
                      </div>

                      {item.message.message ? (
                        <div className="chat__sidebar-chat-history">
                          <p>{item.message.message}</p>
                        </div>
                      ) : null}

                      {item.message.file ? (
                        <div className="chat__sidebar-chat-image">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="15"
                            height="15"
                            fill="none"
                            viewBox="0 0 15 15"
                          >
                            <path
                              fill="currentColor"
                              fillRule="evenodd"
                              d="M2.5 1h10A1.5 1.5 0 0 1 14 2.5v10a1.5 1.5 0 0 1-1.5 1.5h-10A1.5 1.5 0 0 1 1 12.5v-10A1.5 1.5 0 0 1 2.5 1m0 1a.5.5 0 0 0-.5.5v5.864l1.682-1.682a.45.45 0 0 1 .647.01l3.545 3.798 2.808-2.808a.45.45 0 0 1 .636 0L13 9.364V2.5a.5.5 0 0 0-.5-.5zM2 12.5V9.636l1.989-1.988 3.542 3.794L8.941 13H2.5a.5.5 0 0 1-.5-.5m10.5.5h-2.345l-1.672-1.847L11 8.636l2 2V12.5a.5.5 0 0 1-.5.5M6.65 5.5a.85.85 0 1 1 1.7 0 .85.85 0 0 1-1.7 0m.85-1.75a1.75 1.75 0 1 0 0 3.5 1.75 1.75 0 0 0 0-3.5"
                              clipRule="evenodd"
                            ></path>
                          </svg>
                          <p>Image</p>
                        </div>
                      ) : null}

                      <div className="chat__sidebar-chat-badge chat__sidebar-chat-badge__open">Open</div>
                    </div>
                  </div>
                ))
              ) : (
                <EmptyInbox />
              )}
            </div>
          </div>

          {/* HISTORY */}
          <div className={`tab-page ${chatView === 'history' ? 'tab-page__active' : ''}`}>
            <div className="chat__sidebar-chat-list">
              {filteredAndSortedHistory.length ? (
                filteredAndSortedHistory?.map((item) => (
                  <div
                    onClick={() => onInboxSelect(item)}
                    key={item.room.id}
                    className="chat__sidebar-chat-item chat__sidebar-chat-item__selected"
                  >
                    <div className="chat__sidebar-user-icon">
                      <UserIcon />
                    </div>
                    <div className="chat__sidebar-chat-info">
                      <div className="chat__sidebar-info-item">
                        <span className="chat__sidebar-info-item__chat-id">{item.room.name}</span>
                        <span className="chat__sidebar-info-item__chat-time">
                          {formatChatTime(item.message.created_at)}
                        </span>
                      </div>

                      <div className="chat__sidebar-chat-history">
                        <p>{item.message.message}</p>
                      </div>

                      <div className="chat__sidebar-chat-badge chat__sidebar-chat-badge__close">Close</div>
                    </div>
                  </div>
                ))
              ) : (
                <EmptyInbox />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;

const EmptyInbox = () => {
  return (
    <div className="empty-chat">
      <div className="empty-chat__wrapper">
        <svg xmlns="http://www.w3.org/2000/svg" width="114" height="93" fill="none" viewBox="0 0 114 93">
          <path
            fill="#D5D5D5"
            d="M10.364 46.5C4.664 46.5 0 41.85 0 36.167V10.333C0 4.65 4.664 0 10.364 0h41.454c5.7 0 10.364 4.65 10.364 10.333v25.834c0 5.683-4.664 10.333-10.364 10.333H41.455V62L25.909 46.5zm93.272 31c5.7 0 10.364-4.65 10.364-10.333V41.333C114 35.65 109.336 31 103.636 31h-31.09v5.167c0 11.366-9.328 20.666-20.728 20.666v10.334c0 5.683 4.664 10.333 10.364 10.333h10.364V93L88.09 77.5z"
          ></path>
        </svg>
        <p>Belum Ada Aktivitas</p>
      </div>
    </div>
  );
};
